/**
 * Utility functions for handling dates without timezone conversion issues
 */

/**
 * Converts a Date object to a local date string in YYYY-MM-DD format
 * without timezone conversion. This ensures that if a user selects
 * October 21st, it's stored as October 21st regardless of timezone.
 */
export function formatDateForAPI(date: Date | null | undefined): string | undefined {
  if (!date) return undefined;
  
  // Get the local year, month, and day
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Parses a date string (YYYY-MM-DD) as a local date, not UTC.
 * This prevents the "day before" bug when displaying dates.
 */
export function parseDateFromAPI(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  
  // Parse the date as local, not UTC
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Converts a date-only string to ISO string format while preserving the local date.
 * This is used for sending dates to the backend.
 */
export function toLocalDateString(date: Date | string | null | undefined): string | undefined {
  if (!date) return undefined;
  
  if (typeof date === 'string') {
    // If it's already a YYYY-MM-DD string, return it
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    // Otherwise parse it first
    date = new Date(date);
  }
  
  return formatDateForAPI(date);
}
